<template>
	<GridLayout
		:sidebar-state="false"
		right-title="MATERIAIS"
	>
		<template #right-header>
			<div class="w-full h-full flex items-center gap-3">
				<button
					class="btn btn-rounded"
					@click="Previous"
				>
					<i class="fas fa-chevron-left"></i>
				</button>
				<Steps
					:steps-map="stepsMap"
					:current-step="currentStep"
					:sidebar-state="sidebarState"
					:custom-style="{
						paddingX: stepsPadding,
						fontSize: stepsFontSize,
					}"
					@select-step="
						currentStep = $event
						autoNav = false
					"
				/>
			</div>
		</template>
		<template #right-tool-bar>
			<ToolBar class="flex gap-5 justify-between">
				<div class="w-6/12 form-group m-0 position-relative">
					<SearchGroup
						:search="search"
						placeholder="BUSCAR..."
						@search="
							search = $event
							OnSearch()
						"
					/>
				</div>

				<div class="w-full h-full flex justify-end align-items-center gap-3">
					<MaterialTypeCheckboxes
						:value="form.material_type"
						:disabled="isToolbarLocked"
						@input="OnToolbarTypeChange"
					/>
				</div>

				<button
					class="btn btn-tool"
					@click="OpenCreateModal"
				>
					<i class="fal fa-plus mx-2 font-32"></i>
				</button>
			</ToolBar>
		</template>
		<template #right-content>
			<transition
				name="fade"
				mode="out-in"
			>
				<div v-if="step === 'pre'"></div>

				<MaterialGroup
					v-if="step === 'group'"
					ref="materialGroup"
					label="Materiais"
					:groups="filteredGroups"
					:group_id="selected.group_id"
					@update-groups="UpdateGroups"
					@toggle-group-status="HandleToggleGroupStatus"
					@next="WalkStep"
				/>

				<MaterialSubgroup
					v-if="step === 'subgroup'"
					ref="materialSubgroup"
					label="Materiais"
					:parent_id="selected.group_id"
					:subgroups="filteredSubgroups"
					:subgroup_id="selected.subgroup_id"
					@update-subgroups="UpdateSubgroups"
					@toggle-subgroup-status="HandleToggleSubgroupStatus"
					@next="WalkStep"
					@previous="step = 'group'"
				/>

				<MainContent 
					v-if="step === 'materials-list'"
				>
					<DatatablesMaterial
						v-model="materials"
						class="w-full flex-1 flex flex-col px-4"
						:headers="headers"
						:fixed-header="true"
						max_height="75vh"
						:search="search"
						:tr-button="true"
						:checkbox="true"
						checkbox_value="id"
						:show-total="false"
						@checkeds="selectedMaterials = $event"
						@by-click-on-tr="Config"
						@filtered-items-count="showedItemsCount = $event"
					>
						<template v-slot:body="{ item }">
							<td
								scope="col"
								:class="{
									'text-primary': item.status,
									'text-secondary': !item.status,
								}"
							>
								<i
									class="font-40 fad"
									:class="{
										'fa-toggle-on': item.status,
										'fa-toggle-off': !item.status,
									}"
									@click.stop="ToggleMaterialStatus(item)"
								></i>
							</td>
							<td scope="col">
								<b>{{ item.name.toUpperCase() }}</b>
							</td>
							<td scope="col">
								{{ item.material_type.material ? 'MATERIAL' : item.material_type.medical ? 'MEDICAMENTO' : item.material_type.opme ? 'OPME' : item.material_type.medical_gas ? 'GÁS MEDICINAL' : '' }}
							</td>
							<td scope="col">
								{{ item.unit }}
							</td>
							<td scope="col">
								{{ item.internal_code }}
							</td>
							<td scope="col">
								R$
								{{
									item.unity_value &&
									String(item.unity_value).indexOf(',') >
										-1
										? String(item.unity_value)
										: ParseMoney(
												String(item.unity_value)
											)
								}}
							</td>
							<td scope="col">
								R$
								{{
									item.aliquot_value &&
									String(item.aliquot_value).indexOf(
										','
									) > -1
										? String(item.aliquot_value)
										: ParseMoney(
												String(item.aliquot_value)
											)
								}}
							</td>
							<td scope="col">
								R$
								{{
									item.honorary_value &&
									String(item.honorary_value).indexOf(
										','
									) > -1
										? String(item.honorary_value)
										: ParseMoney(
												String(item.honorary_value)
											)
								}}
							</td>
							<td style="width: 10px; padding-right: 0">
								<button
									class="btn btn-unshaped btn-outline-danger font-24"
									@click.stop="DeleteMaterial(item.id)"
								>
									<i class="fal fa-trash"></i>
								</button>
							</td>
						</template>
					</DatatablesMaterial>
				</MainContent>
			</transition>

			<MaterialForm
				:open="open_modal"
				:type="form_type"
				:material="selected.material"
				:parent_id="selected.subgroup_id"
				:context_material_type="newMaterialContext.materialType"
				:context_locked="newMaterialContext.locked"
				:context_locked_level="newMaterialContext.level"
				@refresh="UpdateMaterial"
				@close="open_modal = false"
			/>
		</template>
		<template #right-info-bar>
			<InfoBar 
				v-if="step == 'materials-list'"
				class="flex justify-center"
			>
				<div class="flex items-center gap-2">
					<span>EXIBIDOS: </span>
					<b>{{ showedItemsCount }}</b>
					<span>ITENS</span>
				</div>
			</InfoBar>
		</template>
		<template #right-footer>
			<div class="w-full h-full flex justify-between">
				<button
					class="btn btn-fixed-size btn-danger"
					:disabled="!hasSelected"
					@click="DeleteSelected"
				>
					DELETAR
				</button>
				<button
					class="btn btn-fixed-size btn-success"
					:disabled="!isTypeDirty"
					@click="SaveMaterialTypeChanges"
				>
					SALVAR
				</button>
			</div>
		</template>
	</GridLayout>
</template>

<script>
	import MaterialGroup from './components/MaterialGroup'
	import MaterialSubgroup from './components/MaterialSubgroup'
	import MaterialForm from './components/MaterialForm'
	import DatatablesMaterial from './components/DatatablesMaterial.vue'
	import MaterialTypeCheckboxes from './components/MaterialTypeCheckboxes.vue'
	import { useConfirmation, useDeleteAlert, useError, useEachRecursively, useMaterialTypeWarning } from '@/composables'

	import { mapGetters, mapActions } from 'vuex'

	const _ = require('lodash')

	export default {
		name: 'Materials',
		components: {
			MaterialGroup,
			MaterialSubgroup,
			MaterialForm,
			DatatablesMaterial,
			MaterialTypeCheckboxes,
		},
		data() {
			return {
				form: {
					material_type: {
						material: true,
						medical: false,
						opme: false,
						medical_gas: false,
					},
				},
				groups: [],
				subgroups: [],
				materials: [],
				headers: [
					{
						text: 'STATUS',
						sortable: true,
						value: 'status',
						type: 'status',
					},
					{ text: 'NOME', sortable: true, value: 'name' },
					{ text: 'TIPO', sortable: true, value: 'material_type' },
					{ text: 'UNIDADE', sortable: true, value: 'unit' },
					{ text: 'CÓDIGO', sortable: true, value: 'code' },
					{ text: 'VALOR', sortable: true, value: 'value' },
					{
						text: 'ALÍQUOTA',
						sortable: true,
						value: 'aliquot_value',
					},
					{
						text: 'HONORÁRIO',
						sortable: true,
						value: 'honorary_value',
					},
					{ text: '', sortable: true, value: 'actions' },
				],
				selected: {
					group_id: null,
					subgroup_id: null,
					material_id: null,
					group_name: '',
					subgroup_name: '',
				},
				search: '',
				form_type: 'new', //'config'
				open_modal: false,
				isTypeDirty: false,
				typeChangeSnapshot: null,
				isToolbarLocked: false,
				selectedMaterials: [],
				showedItemsCount: 0,
				sidebarState: false,
				currentStep: 0,
				stepsMap: [
					{
						key: 'pre',
					},
					{
						key: 'group',
						number: 1,
						label: 'GRUPO',
						isValid: false,
					},
					{
						key: 'subgroup',
						number: 2,
						label: 'SUBGRUPO',
						isValid: false,
					},
					{
						key: 'materials-list',
						number: 3,
						label: 'MATERIAL',
						isValid: false,
					},
				],
			}
		},
		computed: {
			...mapGetters('auth', ['requester_selected_id', 'requesters']),
			step() {
				return this.stepsMap[this.currentStep].key
			},
			stepsFontSize() {
				let fontSize = '1rem'

				if (this.sidebarState) {
					fontSize = '0.74rem'

					if (this.isUrgentAppointment) fontSize = '0.65rem'
				}

				return fontSize
			},
			stepsPadding() {
				let padding = '1rem'

				if (this.sidebarState) {
					if (this.isUrgentAppointment) padding = '0.5rem'
				}

				return padding
			},
			hasSelected() {
				if (this.step === 'group') {
					return this.groups.some(g => g.selected)
				} else if (this.step === 'subgroup') {
					return this.subgroups.some(sg => sg.selected)
				} else if (this.step === 'materials-list') {
					return this.selectedMaterials.length > 0
				}
				return false
			},
			filteredGroups() {
				if (!this.search) return this.groups
				const search = this.search.toUpperCase()
				return this.groups.filter(g =>
					(g.name || '').toUpperCase().includes(search)
				)
			},
			filteredSubgroups() {
				if (!this.search) return this.subgroups
				const search = this.search.toUpperCase()
				return this.subgroups.filter(sg =>
					(sg.name || '').toUpperCase().includes(search)
				)
			},
			newMaterialContext() {
				const parentSubgroup = this.subgroups.find(
					sg => sg.id === this.selected.subgroup_id
				)

				return this.ResolveNewItemType(parentSubgroup)
			},
		},
		watch: {
			step: {
				handler(val) {
					let hash = ''

					switch (val) {
						case 'group':
							hash = '#grupo'
							this.selected.group_id = null
							this.selected.group_name = ''
							this.selected.subgroup_id = null
							this.selected.subgroup_name = ''
							this.selected.material = null
							this.selected.material_id = null
							this.subgroups = []
							this.materials = []
							this.stepsMap[2].isValid = false
							break
						case 'subgroup':
							hash = '#subgrupo'
							this.selected.subgroup_id = null
							this.selected.subgroup_name = ''
							this.selected.material = null
							this.selected.material_id = null
							this.materials = []
							break
						case 'materials-list':
							hash = '#lista-de-materiais'
							break
					}

					this.SyncToolbarTypeFromItems()

					window.location.hash = hash

					this.$nextTick(() => {
						if (this.$refs.searchInput) {
							this.$refs.searchInput.focus()
						}
					})
				},
				immediate: true,
			},
			$route(to) {
				switch (to.hash) {
					case '#grupo':
						this.currentStep = 1
						this.selected.group_id = null
						this.stepsMap[2].isValid = false
						break
					case '#subgrupo':
						this.currentStep = 2
						break
					case '#lista-de-materiais':
						this.currentStep = 3
						break
				}
			},
		},
		methods: {
			...mapActions('system', ['StartLoading', 'FinishLoading']),
			OpenCreateModal() {
				switch (this.step) {
					case 'group':
						if (
							this.$refs.materialGroup &&
							this.$refs.materialGroup.New
						) {
							const { materialType, locked, level } =
								this.ResolveNewItemType(null)

							this.$refs.materialGroup.New(materialType, locked, level)
						}
						break
					case 'subgroup':
						if (
							this.$refs.materialSubgroup &&
							this.$refs.materialSubgroup.New
						) {
							const parentGroup = this.groups.find(
								g => g.id === this.selected.group_id
							)
							const { materialType, locked, level } =
								this.ResolveNewItemType(parentGroup)

							this.$refs.materialSubgroup.New(materialType, locked, level)
						}
						break
					case 'materials-list':
						this.form_type = 'new'
						this.selected.material = null
						this.open_modal = true
						break
				}
			},
			async WalkStep(param = null) {
				if (this.isTypeDirty && !(await this.ConfirmTypeChanges())) return

				switch (this.step) {
					case 'pre':
						this.ResetSteps()
						this.currentStep = 1
						break
					case 'group':
						if (param) {
							this.selected.group_id = param.id
							this.selected.group_name = param.name || ''
							this.subgroups = param.children ?? []
							this.selected.subgroup_name = ''
							this.stepsMap[this.currentStep].isValid = true
							this.currentStep++
						}
						break
					case 'subgroup':
						if (param) {
							this.selected.subgroup_id = param.id
							this.selected.subgroup_name = param.name || ''
							this.materials = param.children ?? []
							this.stepsMap[this.currentStep].isValid = true
							this.currentStep++
						}
						break
					case 'materials-list':
						break
				}
			},
			Previous() {
				switch (this.step) {
					case 'group':
						this.$router.push({ name: 'Home' })
						break
					case 'subgroup':
					case 'materials-list':
						this.currentStep--
						break
					default:
						this.$emit('previous')
				}

				this.autoNav = false
			},
			GetMaterials() {
				this.StartLoading()

				return window.api
					.call('post', '/get-materials-by-organization', {
						organization_id: this.requester_selected_id,
					})
					.then(({ data }) => {
						if (data.response) {
							this.groups = data.groups
							this.currentStep = 1
						}
					})
					.finally(() => {
						this.FinishLoading()
					})
			},
			UpdateMaterial({ material, method }) {
				switch (method) {
					case 'create':
						this.materials.push(material)
						this.materials = _.orderBy(
							this.materials,
							'name',
							'asc'
						)
						break
					case 'update':
						this.materials = this.materials.map(p => {
							let new_p = p

							if (p.id == material.id) {
								new_p = material
							}

							return new_p
						})
						break
					case 'delete':
						this.materials = this.materials.filter(
							p => p.id != material.id
						)
						break
				}
			},
			UpdateGroups(groups) {
				this.groups = [...groups]
			},
			UpdateSubgroups(subgroups) {
				this.subgroups = [...subgroups]
			},
			New() {
				this.form_type = 'new'
				this.selected.material = null
				this.open_modal = true
			},
			async Config(material) {
				if (this.isTypeDirty && !(await this.ConfirmTypeChanges())) return

				this.form_type = 'config'
				this.selected.material = material
				this.open_modal = true
			},
			CurrentStepItems() {
				switch (this.step) {
					case 'group':
						return this.groups
					case 'subgroup':
						return this.subgroups
					case 'materials-list':
						return this.materials
					default:
						return []
				}
			},
			ResolveNewItemType(parent) {
				const defaultType = {
					material: true,
					medical: false,
					opme: false,
					medical_gas: false,
				}

				if (parent && parent.material_type && !parent.material_type.material) {
					return {
						materialType: { ...parent.material_type },
						locked: true,
						level: parent._type_locked
							? parent._type_locked_level
							: this.LevelForItemType(parent.type),
					}
				}

				const items = this.CurrentStepItems()
				const allLocked =
					items.length > 0 &&
					items.every(
						item =>
							item._type_locked &&
							_.isEqual(item.material_type, items[0].material_type)
					)

				if (allLocked) {
					return {
						materialType: { ...items[0].material_type },
						locked: true,
						level: items[0]._type_locked_level,
					}
				}

				return { materialType: defaultType, locked: false, level: null }
			},
			LevelForItemType(type) {
				switch (Number(type)) {
					case 1:
						return 'group'
					case 2:
						return 'subgroup'
					case 3:
						return 'materials-list'
					default:
						return null
				}
			},
			OnToolbarTypeChange(materialType) {
				if (this.isToolbarLocked) return

				if (!this.typeChangeSnapshot) {
					const items = []
					useEachRecursively(this.CurrentStepItems(), item => {
						items.push({
							item,
							material_type: _.cloneDeep(item.material_type),
							_type_locked: item._type_locked,
							_type_locked_level: item._type_locked_level,
						})
					})
					this.typeChangeSnapshot = {
						materialType: { ...this.form.material_type },
						items,
					}
				}

				if (_.isEqual(materialType, this.typeChangeSnapshot.materialType)) {
					this.DiscardTypeChanges()
					return
				}

				this.form.material_type = materialType

				const items = this.CurrentStepItems()
				const locked = !materialType.material
				const level = this.step

				useEachRecursively(items, item => {
					item.material_type = { ...materialType }
					item._type_locked = locked
					item._type_locked_level = locked ? level : null
				})

				this.isTypeDirty = true
			},
			DiscardTypeChanges() {
				if (!this.typeChangeSnapshot) return

				const { materialType, items } = this.typeChangeSnapshot
				items.forEach(({ item, ...original }) => {
					Object.assign(item, original)
				})
				this.form.material_type = { ...materialType }
				this.typeChangeSnapshot = null
				this.isTypeDirty = false
			},
			SyncToolbarTypeFromItems() {
				this.DiscardTypeChanges()
				const items = this.CurrentStepItems()
				const allLocked =
					items.length > 0 &&
					items.every(
						item =>
							item._type_locked &&
							_.isEqual(item.material_type, items[0].material_type)
					)

				this.form.material_type = allLocked
					? { ...items[0].material_type }
					: {
							material: true,
							medical: false,
							opme: false,
							medical_gas: false,
					  }

				this.isToolbarLocked = allLocked && items[0]._type_locked_level !== this.step

				this.isTypeDirty = false
			},
			async ConfirmTypeChanges() {
				const { isConfirmed, isDenied } = await useMaterialTypeWarning({ returnResult: true })
				if (isDenied) {
					this.DiscardTypeChanges()
					return true
				}
				if (!isConfirmed) return false
				return this.SaveMaterialTypeChanges(true)
			},
			async SaveMaterialTypeChanges(confirmed = false) {
				if (!this.isTypeDirty) return true
				if (confirmed !== true && !(await useMaterialTypeWarning())) return false

				const items = this.CurrentStepItems()
				const materialType = this.form.material_type

				this.StartLoading()

				return window.api
					.call('post', '/save-materials-type', {
						organization_id: this.requester_selected_id,
						material_ids: items.map(item => item.id),
						material_type: materialType,
						level: this.step,
					})
					.then(({ data }) => {
						if (data.response) {
							this.isTypeDirty = false
							this.typeChangeSnapshot = null

							useConfirmation()
							return true
						} else {
							useError(
								'Houve um erro ao concluir, contate o suporte.'
							)
						}
					})
					.finally(() => {
						this.FinishLoading()
					})
			},
			DeleteMaterial(id) {
				useDeleteAlert().then(async result => {
					if (result.isConfirmed) {
						this.StartLoading()
						await window.api
							.call('post', '/delete-material', {
								organization_id: this.requester_selected_id,
								material_id: id,
							})
							.then(({ data }) => {
								if (data.response) {
									useConfirmation()

									this.materials = this.materials.filter(
										m => m.id !== id
									)
								} else {
									useError(
										'Houve um erro ao concluir, contate o suporte.'
									)
								}
							})
							.finally(() => {
								this.FinishLoading()
							})
					}
				})
			},
			async DeleteSelected() {
				let ids = []

				if (this.step === 'group') {
					ids = this.groups.filter(g => g.selected).map(g => g.id)
				} else if (this.step === 'subgroup') {
					ids = this.subgroups
						.filter(sg => sg.selected)
						.map(sg => sg.id)
				} else if (this.step === 'materials-list') {
					ids = this.selectedMaterials
				}

				useDeleteAlert().then(async result => {
					if (result.isConfirmed) {
						this.StartLoading()

						let deleted = []

						return window.api
							.call('post', '/delete-material', {
								organization_id: this.requester_selected_id,
								material_id: ids,
							})
							.then(({ data }) => {
								if (data.response) {
									deleted = ids

									if (this.step === 'group') {
										this.groups = this.groups.filter(
											g => !deleted.includes(g.id)
										)
									} else if (this.step === 'subgroup') {
										this.subgroups = this.subgroups.filter(
											sg => !deleted.includes(sg.id)
										)
									} else if (this.step === 'materials-list') {
										this.materials = this.materials.filter(
											m => !deleted.includes(m.id)
										)
									}

									useConfirmation()
								} else {
									useError(
										'Houve um erro ao concluir, contate o suporte.'
									)
								}
							})
							.finally(() => {
								this.FinishLoading()
							})
					}
				})
			},
			ToggleMaterialStatus(material) {
				const vm = this

				vm.StartLoading()

				const steps_selected = {
					group_id: vm.selected.group_id,
					subgroup_id: vm.selected.subgroup_id,
				}

				return window.api
					.call('post', '/toggle-material-status', {
						organization_id: vm.requester_selected_id,
						material_id: material.id,
					})
					.then(async ({ data }) => {
						if (data.response) {
							material.status = !material.status

							if (material.type === 1) {
								vm.subgroups = vm.subgroups.map(sg => {
									if (sg.parent_id === material.id) {
										sg.status = material.status
									}
									return sg
								})

								vm.materials = vm.materials.map(m => {
									const subgrupo = vm.subgroups.find(
										sg => sg.id === m.parent_id
									)
									if (
										subgrupo &&
										subgrupo.parent_id === material.id
									) {
										m.status = material.status
									}
									return m
								})
							}

							if (material.type === 2) {
								vm.materials = vm.materials.map(m => {
									if (m.parent_id === material.id) {
										m.status = material.status
									}
									return m
								})
							}

							vm.UpdateMaterial(material)

							return
						}
					})
					.finally(() => {
						vm.FinishLoading()
					})
			},
			HandleToggleGroupStatus({ id, status }) {
				const groupId = id

				this.subgroups = this.subgroups.map(sg => {
					if (sg.parent_id == groupId) {
						const newSg = { ...sg, status: status }

						if (newSg.children && Array.isArray(newSg.children)) {
							newSg.children = newSg.children.map(mat => ({
								...mat,
								status: status,
							}))
						}

						return newSg
					}

					return sg
				})

				this.materials = this.materials.map(m => {
					if (m.parent_id == groupId || m.group_id == groupId) {
						return { ...m, status: status }
					}

					const sub = this.subgroups.find(sg => sg.id == m.parent_id)

					if (sub && sub.parent_id == groupId) {
						return { ...m, status: status }
					}

					return m
				})

				this.groups = this.groups.map(g => {
					if (g.id == groupId) {
						const newG = { ...g, status: status }

						if (newG.children && Array.isArray(newG.children)) {
							newG.children = newG.children.map(sg => {
								const newSg = { ...sg, status: status }

								if (
									newSg.children &&
									Array.isArray(newSg.children)
								) {
									newSg.children = newSg.children.map(
										mat => ({ ...mat, status: status })
									)
								}

								return newSg
							})
						}

						return newG
					}

					return g
				})
			},
			HandleToggleSubgroupStatus({ id, status }) {
				this.materials = this.materials.map(m => {
					if (m.parent_id === id) {
						m.status = status
					}
					return m
				})

				this.subgroups = this.subgroups.map(sg => {
					if (
						sg.id === id &&
						sg.children &&
						Array.isArray(sg.children)
					) {
						sg.children = sg.children.map(mat => {
							return { ...mat, status: status }
						})
					}
					return sg
				})

				const parentId = this.subgroups.find(s => s.id == id)?.parent_id

				if (parentId) {
					this.groups = this.groups.map(g => {
						if (
							g.id == parentId &&
							g.children &&
							Array.isArray(g.children)
						) {
							const newG = { ...g }

							newG.children = newG.children.map(sg => {
								if (sg.id == id) {
									const newSg = { ...sg, status: status }

									if (
										newSg.children &&
										Array.isArray(newSg.children)
									) {
										newSg.children = newSg.children.map(
											mat => ({ ...mat, status: status })
										)
									}

									return newSg
								}

								return sg
							})

							return newG
						}
						return g
					})
				}
			},
			ParseMoney(value) {
				const numValue = parseFloat(value)

				return numValue
					.toFixed(2)
					.replace('.', ',')
					.replace(/\B(?=(\d{3})+(?!\d))/g, '.')
			},
			onSearch() {
				this.$forceUpdate()
			},
		},
		async beforeMount() {
			await this.GetMaterials()
			this.WalkStep()
		},
	}
</script>

<style lang="scss" scoped>
	.breadcrumb-bar {
		display: flex;
		flex-direction: row;
		justify-content: center;
		align-items: center;
		height: 4rem;
		background: var(--barsBackground);
		font-size: 22px;
		color: var(--titleFontColor);
		font-weight: bold;
		border-bottom: 1px solid var(--separatorColor);

		.breadcrumb-link {
			color: var(--titleFontColor);
			background: var(--barsBackground);
			border: none;
			padding-left: 40px;
			padding-right: 40px;
			font-size: 16px;
			font-weight: 700;

			&.inactive {
				color: #aaa;
				background: var(--barsBackground);
				opacity: 0.7;
			}

			&.active {
				color: var(--titleFontColor);
				opacity: 1;
			}
		}
	}

	.materials-wrapper {
		width: 100%;
		height: 100%;
		position: fixed;
		left: 0;
		background: var(--graphBackgroundColor);

		.materials-layout {
			display: flex;
			width: 100vw;
			height: calc(100vh - 6.235rem);
		}

		.materials-list-mode {
			overflow: unset;
		}

		.materials-content {
			flex: 1 1 75vw;
			width: 75vw;
			min-width: 0;
			height: calc(100% - 3.75rem);
			display: flex;
			flex-direction: column;
			background-color: var(--graphBackgroundColor);
			overflow-y: auto;
			padding-bottom: 2rem;
			transition: width 0.3s;
		}

		.materials-list-mode {
			overflow: unset;
		}

		.sub-footer {
			position: fixed;
			bottom: 3.75rem;
			width: 100%;
			padding: 0.5rem;
			display: flex;
			flex-direction: column;
			align-items: center;
			background-color: var(--barsBackground);
			border-top: 1px solid var(--separatorColor);
			border-bottom: 1px solid var(--separatorColor);
		}

		.footer {
			position: fixed;
			bottom: 0;
			left: 0;
			background-color: var(--barsBackground);
			color: var(--graphBackgroundColor);
			padding: 0.375rem 0.938rem;
			text-align: left;
			width: 100%;
			height: 3.75rem;
			z-index: 10;
		}

		.menu-btn {
			background: var(--barsBackground);
			border: none;
			cursor: pointer;
			padding: 0px;
		}

		.side-menu-overlay {
			position: fixed;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background: var(--barsBackground);
			z-index: 100;
			display: flex;
			justify-content: flex-end;
		}

		.title {
			display: flex;
			flex-direction: row;
			justify-content: center;
			align-items: center;
			height: 3.125rem;
			background: var(--barsBackground);
			font-size: 22px;
			color: var(--titleFontColor);
			font-weight: bold;
			border-bottom: 1px solid var(--separatorColor);
		}

		.subtitle {
			display: flex;
			flex-direction: row;
			align-items: center;
			height: 3.125rem;
			background: var(--barsBackground);
			font-size: 18px;
			color: var(--titleFontColor);
			position: relative;
			padding: 0 1rem;
		}

		.subtitle-flex {
			justify-content: space-between;
		}

		.subtitle-btn-wrapper {
			flex: 1 0 auto;
			display: flex;
			align-items: center;
		}

		.subtitle-btn-right {
			flex: 0 0 auto;
			display: flex;
			align-items: center;
			gap: 1rem;
		}

		.subtitle-center-text {
			flex: 1 1 0;
			text-align: center;
			font-size: 18px;
			font-weight: 500;
			letter-spacing: 0.5px;
		}

		.subtitle-btn-placeholder {
			flex: 0 0 90px;
		}

		.text-primary {
			color: var(--newPrimaryBackgroundColor) !important;
		}

		.btn-light-create,
		.btn-light-create.disabled,
		.btn-light-create:disabled {
			border-radius: 50% !important;
			width: 44px;
			height: 44px;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 0;
			color: #fff;
			background-color: var(--newPrimaryBackgroundColor);
			border-color: var(--newPrimaryBackgroundColor);
			filter: grayscale(0.4);
		}

		.btn-light-create:hover,
		a.btn-light-create:hover,
		.btn-light-create:not(:disabled):not(.disabled).active,
		.btn-light-create:not(:disabled):not(.disabled):active,
		.btn-light-create.focus,
		.btn-light-create:focus {
			color: #fff !important;
			background-color: var(--buttonPrimaryHover);
			border-color: var(--buttonPrimaryHover);
		}

		.btn-sm {
			padding: 0.45rem 1rem !important;
		}

		.btn-danger {
			position: absolute;
			left: 1.5rem;
			top: 50%;
			transform: translateY(-50%);
			height: 2.8rem;
			font-size: 1.1rem;
			font-weight: bold;
			display: flex;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			background-color: #b71c1c;
			border-color: #b71c1c;
			border-radius: 0.8rem !important;

			&:hover {
				background-color: hsl(0, 73%, 35%) !important;
				border-color: hsl(0, 73%, 35%) !important;
			}
		}

		.fad.fa-toggle-on:after {
			color: var(--white);
			opacity: 1;
		}

		.btn-unshaped {
			background: none;
			border: none;
			padding: 0.5rem;
			cursor: pointer;
		}

		.btn-outline-danger {
			border-radius: 0.5rem;
			font-size: 1.5rem;
			transition: background 0.2s;

			&:hover {
				background: #b71c1c;
			}
		}

		font-40 {
			font-size: 2rem;
		}

		.btn-light-search,
		.btn-light-search.disabled,
		.btn-light-search:disabled {
			border-radius: 8px;
			width: 45px;
			height: 44px;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 0;
			color: #fff;
			background-color: var(--newPrimaryBackgroundColor);
			border-color: var(--newPrimaryBackgroundColor);
			filter: grayscale(0.4);
		}

		.btn-light-search:hover,
		a.btn-light-search:hover,
		.btn-light-search:not(:disabled):not(.disabled).active,
		.btn-light-search:not(:disabled):not(.disabled):active {
			color: #fff !important;
			background-color: var(--buttonPrimaryHover);
			border-color: var(--buttonPrimaryHover);
		}
	}

	.table-custom tbody tr td:nth-child(2) {
		padding-left: 2.2rem;
		border-top-left-radius: 1rem !important;
		border-bottom-left-radius: 1rem !important;
	}

	.form-group {
		margin-bottom: 0;
	}
</style>
